/**
 * TimelineGantt — integrated accordion-Gantt component.
 *
 * Combines the node tree from AccordionNodeList with the Gantt chart from
 * GanttTimeline into a single component where the accordion IS the Gantt's
 * label column.  Every visible accordion row = a Gantt row.  The whole thing
 * scrolls as one unit with a sticky left label column.
 *
 * Tooltip uses pure SVG <rect> + <text> (no foreignObject) for fully opaque
 * rendering.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import { useAdjacencyStore } from '../../store/adjacency-store';
import { NodeModal } from '../editors/NodeModal';
import { INITIATIVE_COLORS } from '../../utils/map-colors';
import type { BudgetProject } from '../../utils/budget-import';
import budgetData from '../../data/budget-2026.json';

const budgetProjects = budgetData.projects as BudgetProject[];

// ── Constants ──────────────────────────────────────────────────────────

const ROW_HEIGHT = 30;
const LABEL_WIDTH = 360;
const MIN_CHART_WIDTH = 400;
const TOP_PAD = 32;
const BOTTOM_PAD = 8;
const RIGHT_PAD = 20;

// ── Types ──────────────────────────────────────────────────────────────

interface Props {
  nodes: AdjacencyNode[];
  allNodes: AdjacencyNode[];
}

interface TreeNode {
  node: AdjacencyNode;
  children: TreeNode[];
}

type DisplayRow =
  | { kind: 'initiative'; initiative: string; nodeCount: number; isOpen: boolean }
  | {
      kind: 'node';
      node: AdjacencyNode;
      depth: number;
      hasChildren: boolean;
      isExpanded: boolean;
      budgetPriority: number;
    };

type MilestoneType = 'ATD' | 'ATQ' | 'ATS' | 'MRD';

const MILESTONE_COLORS: Record<MilestoneType, string> = {
  ATD: '#f97316',
  ATQ: '#22c55e',
  ATS: '#3b82f6',
  MRD: '#ef4444',
};

// ── Initiative group ordering ──────────────────────────────────────────

const INITIATIVE_ORDER = [
  'US Mining',
  'Proximity Detection',
  'Analytics',
  'Maritime',
  'International Mining',
  'New Verticals',
  'Capabilities',
  'Other',
];

// ── Tree builder (from AccordionNodeList) ──────────────────────────────

function buildNodeTree(
  nodes: AdjacencyNode[],
  allNodes: AdjacencyNode[],
): Map<string, TreeNode[]> {
  const nodeMap = new Map<string, AdjacencyNode>();
  for (const n of allNodes) nodeMap.set(n.id, n);

  const childrenOf = new Map<string, Set<string>>();
  for (const n of nodes) {
    for (const pid of n.parentIds) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, new Set());
      childrenOf.get(pid)!.add(n.id);
    }
    for (const edge of n.edges) {
      if (edge.relationship === 'child') {
        if (!childrenOf.has(n.id)) childrenOf.set(n.id, new Set());
        childrenOf.get(n.id)!.add(edge.targetId);
      }
      if (edge.relationship === 'parent') {
        if (!childrenOf.has(edge.targetId)) childrenOf.set(edge.targetId, new Set());
        childrenOf.get(edge.targetId)!.add(n.id);
      }
    }
  }

  const isChild = new Set<string>();
  for (const kids of childrenOf.values()) {
    for (const cid of kids) isChild.add(cid);
  }

  const filteredSet = new Set(nodes.map((n) => n.id));

  function buildSubTree(nodeId: string, visited: Set<string>): TreeNode | null {
    if (visited.has(nodeId)) return null;
    const n = nodeMap.get(nodeId);
    if (!n || !filteredSet.has(n.id)) return null;
    visited.add(nodeId);

    const kids = childrenOf.get(nodeId);
    const children: TreeNode[] = [];
    if (kids) {
      for (const cid of kids) {
        const child = buildSubTree(cid, visited);
        if (child) children.push(child);
      }
      children.sort((a, b) => a.node.name.localeCompare(b.node.name));
    }
    return { node: n, children };
  }

  const rootNodes = nodes.filter((n) => !isChild.has(n.id));
  const globalVisited = new Set<string>();
  const grouped = new Map<string, TreeNode[]>();
  for (const root of rootNodes) {
    const tree = buildSubTree(root.id, globalVisited);
    if (!tree) continue;
    const group = root.strategicInitiative || 'Other';
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(tree);
  }

  for (const trees of grouped.values()) {
    trees.sort((a, b) => a.node.name.localeCompare(b.node.name));
  }
  return grouped;
}

// ── Flatten tree into display rows ─────────────────────────────────────

function countAll(trees: TreeNode[]): number {
  let n = 0;
  function walk(t: TreeNode) { n++; t.children.forEach(walk); }
  trees.forEach(walk);
  return n;
}

function flattenTree(
  sortedGroups: { initiative: string; trees: TreeNode[] }[],
  expandedInitiatives: Set<string>,
  expandedNodes: Set<string>,
  bpMap: Map<string, BudgetProject>,
): DisplayRow[] {
  const rows: DisplayRow[] = [];

  for (const { initiative, trees } of sortedGroups) {
    const nodeCount = countAll(trees);
    const isOpen = expandedInitiatives.has(initiative);
    rows.push({ kind: 'initiative', initiative, nodeCount, isOpen });

    if (!isOpen) continue;

    function walk(tree: TreeNode, depth: number) {
      const bp = tree.node.budgetProjectName ? bpMap.get(tree.node.budgetProjectName) : null;
      rows.push({
        kind: 'node',
        node: tree.node,
        depth,
        hasChildren: tree.children.length > 0,
        isExpanded: expandedNodes.has(tree.node.id),
        budgetPriority: bp?.budgetPriority ?? 999,
      });

      if (expandedNodes.has(tree.node.id)) {
        for (const child of tree.children) {
          walk(child, depth + 1);
        }
      }
    }

    for (const tree of trees) walk(tree, 0);
  }

  return rows;
}

// ── Status pill color helper ───────────────────────────────────────────

function statusPillColor(status: string): { bg: string; text: string } {
  switch (status) {
    case 'In Progress':
      return { bg: 'rgba(34,197,94,0.15)', text: '#86efac' };
    case 'Complete':
    case 'Completed':
      return { bg: 'rgba(59,130,246,0.15)', text: '#93c5fd' };
    case 'On Hold':
      return { bg: 'rgba(234,179,8,0.15)', text: '#fde047' };
    case 'Not Started':
      return { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' };
    case 'Proposed/Pending':
      return { bg: 'rgba(168,85,247,0.15)', text: '#c4b5fd' };
    case 'Blocked':
      return { bg: 'rgba(239,68,68,0.15)', text: '#fca5a5' };
    default:
      return { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8' };
  }
}

// ── Date formatter ─────────────────────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// ── Component ──────────────────────────────────────────────────────────

export function TimelineGantt({ nodes, allNodes }: Props) {
  const {
    updateNode, deleteNode, duplicateNode,
    createParentChildLink, createRelatedLink, removeRelationship, convertEdgeType,
  } = useAdjacencyStore();
  const [expandedInitiatives, setExpandedInitiatives] = useState<Set<string>>(new Set());
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<{ nodeId: string; mx: number } | null>(null);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(MIN_CHART_WIDTH);

  // Measure the chart area dynamically
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width) - RIGHT_PAD;
        setChartWidth(Math.max(MIN_CHART_WIDTH, w));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Budget priority map
  const bpMap = useMemo(() => {
    const m = new Map<string, BudgetProject>();
    for (const bp of budgetProjects) m.set(bp.project, bp);
    return m;
  }, []);

  // Build tree
  const groupedTree = useMemo(() => buildNodeTree(nodes, allNodes), [nodes, allNodes]);
  const sortedGroups = useMemo(
    () =>
      INITIATIVE_ORDER.filter((g) => groupedTree.has(g)).map((g) => ({
        initiative: g,
        trees: groupedTree.get(g)!,
      })),
    [groupedTree],
  );

  // Flatten to display rows
  const displayRows = useMemo(
    () => flattenTree(sortedGroups, expandedInitiatives, expandedNodes, bpMap),
    [sortedGroups, expandedInitiatives, expandedNodes, bpMap],
  );

  // Toggle helpers
  const toggleInitiative = useCallback((initiative: string) => {
    setExpandedInitiatives((prev) => {
      const next = new Set(prev);
      if (next.has(initiative)) next.delete(initiative);
      else next.add(initiative);
      return next;
    });
  }, []);

  const toggleNode = useCallback((id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    // Expand all initiatives
    const allInits = new Set(sortedGroups.map((g) => g.initiative));
    setExpandedInitiatives(allInits);
    // Expand all nodes with children
    const allIds = new Set<string>();
    function collect(tree: TreeNode) {
      if (tree.children.length > 0) allIds.add(tree.node.id);
      tree.children.forEach(collect);
    }
    for (const group of sortedGroups) group.trees.forEach(collect);
    setExpandedNodes(allIds);
  }, [sortedGroups]);

  const collapseAll = useCallback(() => {
    setExpandedInitiatives(new Set());
    setExpandedNodes(new Set());
  }, []);

  // ── Timeline range (computed from ALL nodes that have timeline data) ──

  const timelineNodes = useMemo(
    () => nodes.filter((n) => n.startDate && n.endDate),
    [nodes],
  );

  const { minDate, maxDate, quarters } = useMemo(() => {
    if (timelineNodes.length === 0) {
      return { minDate: new Date(), maxDate: new Date(), quarters: [] };
    }

    let min = new Date(timelineNodes[0].startDate!);
    let max = new Date(timelineNodes[0].endDate!);
    for (const n of timelineNodes) {
      const s = new Date(n.startDate!);
      const e = new Date(n.endDate!);
      if (s < min) min = s;
      if (e > max) max = e;
    }

    const qStart = new Date(min.getFullYear(), Math.floor(min.getMonth() / 3) * 3, 1);
    const qEnd = new Date(max.getFullYear(), Math.ceil((max.getMonth() + 1) / 3) * 3, 1);

    const qs: { label: string; date: Date }[] = [];
    const cur = new Date(qStart);
    while (cur <= qEnd) {
      const q = Math.floor(cur.getMonth() / 3) + 1;
      qs.push({ label: `Q${q} ${cur.getFullYear()}`, date: new Date(cur) });
      cur.setMonth(cur.getMonth() + 3);
    }

    return { minDate: qStart, maxDate: qEnd, quarters: qs };
  }, [timelineNodes]);

  const totalMs = maxDate.getTime() - minDate.getTime();
  const toX = (date: Date) => {
    if (totalMs === 0) return 0;
    return ((date.getTime() - minDate.getTime()) / totalMs) * chartWidth;
  };

  const today = new Date();
  const todayX = toX(today);
  const todayInRange = today >= minDate && today <= maxDate;

  const svgHeight = TOP_PAD + displayRows.length * ROW_HEIGHT + BOTTOM_PAD;
  const hasTimeline = timelineNodes.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b shrink-0"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <h3 className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
          Projects ({nodes.length})
        </h3>
        <div className="flex items-center gap-4">
          {/* Legend */}
          {hasTimeline && (
            <div className="flex gap-3 text-[10px]" style={{ color: 'var(--text-dim)' }}>
              {(['ATD', 'ATQ', 'ATS', 'MRD'] as MilestoneType[]).map((type) => (
                <span key={type} className="inline-flex items-center gap-1">
                  <svg width={8} height={8}>
                    <polygon points="4,0 8,4 4,8 0,4" fill={MILESTONE_COLORS[type]} />
                  </svg>
                  {type}
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="text-[10px] hover:underline"
              style={{ color: 'var(--text-dim)' }}
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="text-[10px] hover:underline"
              style={{ color: 'var(--text-dim)' }}
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable area — single scroll container for both columns */}
      <div className="flex-1 overflow-auto min-h-0" ref={scrollRef}>
        <div
          style={{
            display: 'flex',
            minWidth: hasTimeline ? LABEL_WIDTH + MIN_CHART_WIDTH + RIGHT_PAD : LABEL_WIDTH,
          }}
        >
          {/* Left: HTML label column — sticky */}
          <div
            style={{
              width: LABEL_WIDTH,
              flexShrink: 0,
              position: 'sticky',
              left: 0,
              zIndex: 2,
              background: 'var(--canvas-bg, #0f172a)',
              borderRight: '1px solid var(--glass-border)',
            }}
          >
            {/* Column headers */}
            <div
              style={{ height: TOP_PAD, borderColor: 'var(--glass-border)' }}
              className="flex items-end pb-1 border-b text-[11px]"
              // matches quarter label style: fontSize 11, fontWeight 500, --text-muted
            >
              <span style={{ width: 30, flexShrink: 0, paddingLeft: 8, color: 'var(--text-muted)', fontWeight: 500 }}>#</span>
              <span className="flex-1 truncate" style={{ paddingLeft: 4, color: 'var(--text-muted)', fontWeight: 500 }}>Project</span>
              <span style={{ width: 75, flexShrink: 0, textAlign: 'right', paddingRight: 4, color: 'var(--text-muted)', fontWeight: 500 }}>Phase</span>
              <span style={{ width: 80, flexShrink: 0, textAlign: 'right', paddingRight: 8, color: 'var(--text-muted)', fontWeight: 500 }}>Status</span>
            </div>

            {/* Rows */}
            {displayRows.map((row) => {
              if (row.kind === 'initiative') {
                return (
                  <div
                    key={`init-${row.initiative}`}
                    style={{ height: ROW_HEIGHT }}
                    className="flex items-center gap-2 px-2 cursor-pointer"
                    onClick={() => toggleInitiative(row.initiative)}
                  >
                    <ChevronRight
                      size={12}
                      className="shrink-0"
                      style={{
                        transform: row.isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        color: 'var(--text-dim)',
                        transition: 'transform 150ms',
                      }}
                    />
                    <span
                      className="text-[11px] font-semibold"
                      style={{
                        color:
                          INITIATIVE_COLORS[
                            row.initiative as keyof typeof INITIATIVE_COLORS
                          ] ?? 'var(--text-muted)',
                      }}
                    >
                      {row.initiative}
                    </span>
                    <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
                      ({row.nodeCount})
                    </span>
                  </div>
                );
              }

              // Node row
              const isHovered = hover?.nodeId === row.node.id;
              const nodeHasTimeline = !!(row.node.startDate && row.node.endDate);

              return (
                <div
                  key={row.node.id}
                  style={{
                    height: ROW_HEIGHT,
                    background: isHovered ? 'rgba(59,130,246,0.08)' : 'transparent',
                  }}
                  className="flex items-center cursor-pointer transition-colors"
                  onMouseEnter={() => setHover({ nodeId: row.node.id, mx: 0 })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => row.hasChildren && toggleNode(row.node.id)}
                  onDoubleClick={() => setDetailNodeId(row.node.id)}
                >
                  {/* # column */}
                  <span
                    className="text-[11px] tabular-nums"
                    style={{ width: 30, flexShrink: 0, paddingLeft: 8, color: 'var(--text-muted)', fontWeight: 500 }}
                  >
                    {row.budgetPriority < 999 ? row.budgetPriority : ''}
                  </span>

                  {/* Project column — indent, chevron, dot, name */}
                  <div
                    className="flex-1 flex items-center gap-1.5 min-w-0"
                    style={{ paddingLeft: `${row.depth * 18}px` }}
                  >
                    {/* Chevron */}
                    {row.hasChildren ? (
                      <ChevronRight
                        size={12}
                        className="shrink-0"
                        style={{
                          transform: row.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          color: 'var(--text-dim)',
                          transition: 'transform 150ms',
                        }}
                      />
                    ) : (
                      <span style={{ width: 12 }} className="shrink-0" />
                    )}

                    {/* Timeline dot */}
                    <span
                      className="shrink-0 rounded-full"
                      style={{
                        width: 5,
                        height: 5,
                        background: nodeHasTimeline ? '#3b82f6' : 'rgba(255,255,255,0.12)',
                      }}
                    />

                    {/* Node name */}
                    <span
                      className="text-[11px] truncate"
                      style={{
                        color: isHovered ? 'var(--text-primary)' : 'var(--text-secondary)',
                      }}
                    >
                      {row.node.name}
                    </span>
                  </div>

                  {/* Phase column */}
                  <span style={{ width: 75, flexShrink: 0 }} className="flex justify-end pr-1">
                    {row.node.phase && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}
                      >
                        {row.node.phase}
                      </span>
                    )}
                  </span>

                  {/* Status column */}
                  <span style={{ width: 80, flexShrink: 0 }} className="flex justify-end pr-2">
                    {row.node.status && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{
                          background: statusPillColor(row.node.status).bg,
                          color: statusPillColor(row.node.status).text,
                        }}
                      >
                        {row.node.status}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Right: SVG chart area — fills remaining width */}
          {hasTimeline && (
            <div ref={chartRef} style={{ flex: 1, minWidth: MIN_CHART_WIDTH + RIGHT_PAD }}>
            <svg
              width={chartWidth + RIGHT_PAD}
              height={svgHeight}
              className="block"
            >
              {/* Quarter grid lines and labels */}
              {quarters.map((q, i) => {
                const x = toX(q.date);
                return (
                  <g key={i}>
                    <line
                      x1={x}
                      y1={TOP_PAD - 4}
                      x2={x}
                      y2={svgHeight}
                      stroke="rgba(255,255,255,0.06)"
                      strokeWidth={1}
                    />
                    <text x={x + 4} y={TOP_PAD - 10} fill="var(--text-muted)" fontSize={11} fontWeight={500}>
                      {q.label}
                    </text>
                  </g>
                );
              })}

              {/* Today marker */}
              {todayInRange && (
                <>
                  <line
                    x1={todayX}
                    y1={TOP_PAD}
                    x2={todayX}
                    y2={svgHeight}
                    stroke="#ef4444"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                  />
                  <text
                    x={todayX + 3}
                    y={TOP_PAD - 10}
                    fill="#ef4444"
                    fontSize={9}
                    fontWeight={600}
                  >
                    Today
                  </text>
                </>
              )}

              {/* Rows — one per displayRow */}
              {displayRows.map((row, i) => {
                const y = TOP_PAD + i * ROW_HEIGHT;

                // Initiative header row: subtle separator line
                if (row.kind === 'initiative') {
                  return (
                    <line
                      key={`sep-${row.initiative}`}
                      x1={0}
                      y1={y + ROW_HEIGHT - 0.5}
                      x2={chartWidth + RIGHT_PAD}
                      y2={y + ROW_HEIGHT - 0.5}
                      stroke="rgba(255,255,255,0.06)"
                      strokeWidth={1}
                    />
                  );
                }

                // Node row — only render bar if it has timeline data
                const n = row.node;
                if (!n.startDate || !n.endDate) return null;

                const start = new Date(n.startDate);
                const end = new Date(n.endDate);
                const x1 = toX(start);
                const x2 = toX(end);
                const barWidth = Math.max(2, x2 - x1);
                const barY = y + 7;
                const barH = ROW_HEIGHT - 14;
                const isHovered = hover?.nodeId === n.id;
                const color =
                  INITIATIVE_COLORS[n.strategicInitiative as keyof typeof INITIATIVE_COLORS] ??
                  '#6b7280';

                // Milestones
                const milestones: { type: MilestoneType; date: Date }[] = [];
                if (n.atdDate) milestones.push({ type: 'ATD', date: new Date(n.atdDate) });
                if (n.atqDate) milestones.push({ type: 'ATQ', date: new Date(n.atqDate) });
                if (n.atsDate) milestones.push({ type: 'ATS', date: new Date(n.atsDate) });
                if (n.mrdDate) milestones.push({ type: 'MRD', date: new Date(n.mrdDate) });

                return (
                  <g
                    key={n.id}
                    onMouseEnter={(e) => {
                      const svg = e.currentTarget.closest('svg');
                      if (!svg) return;
                      const pt = svg.createSVGPoint();
                      pt.x = e.clientX;
                      pt.y = e.clientY;
                      const svgPt = pt.matrixTransform(svg.getScreenCTM()!.inverse());
                      setHover({ nodeId: n.id, mx: svgPt.x });
                    }}
                    onMouseMove={(e) => {
                      const svg = e.currentTarget.closest('svg');
                      if (!svg) return;
                      const pt = svg.createSVGPoint();
                      pt.x = e.clientX;
                      pt.y = e.clientY;
                      const svgPt = pt.matrixTransform(svg.getScreenCTM()!.inverse());
                      setHover({ nodeId: n.id, mx: svgPt.x });
                    }}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Row background on hover */}
                    {isHovered && (
                      <rect
                        x={0}
                        y={y}
                        width={chartWidth + RIGHT_PAD}
                        height={ROW_HEIGHT}
                        fill="rgba(255,255,255,0.03)"
                      />
                    )}

                    {/* Bar */}
                    <rect
                      x={x1}
                      y={barY}
                      width={barWidth}
                      height={barH}
                      rx={3}
                      fill={color}
                      fillOpacity={isHovered ? 0.9 : 0.6}
                      stroke={color}
                      strokeWidth={isHovered ? 1.5 : 0}
                    />

                    {/* Milestone diamonds */}
                    {milestones.map((ms, j) => {
                      const mx = toX(ms.date);
                      const my = y + ROW_HEIGHT / 2;
                      return (
                        <polygon
                          key={j}
                          points={`${mx},${my - 5} ${mx + 5},${my} ${mx},${my + 5} ${mx - 5},${my}`}
                          fill={MILESTONE_COLORS[ms.type]}
                          stroke="rgba(0,0,0,0.3)"
                          strokeWidth={0.5}
                        />
                      );
                    })}

                  </g>
                );
              })}

              {/* Tooltip layer — renders LAST so it's always on top */}
              {hover && (() => {
                const rowIndex = displayRows.findIndex(
                  (r) => r.kind === 'node' && r.node.id === hover.nodeId
                );
                if (rowIndex === -1) return null;
                const row = displayRows[rowIndex];
                if (row.kind !== 'node') return null;
                const n = row.node;
                if (!n.startDate || !n.endDate) return null;

                const y = TOP_PAD + rowIndex * ROW_HEIGHT;
                const tx = Math.max(0, Math.min(hover.mx - 115, chartWidth - 230));
                const lines = [
                  n.name,
                  `Start: ${formatDate(n.startDate)}`,
                  `End: ${formatDate(n.endDate)}`,
                  ...(n.atdDate ? [`ATD: ${formatDate(n.atdDate)}`] : []),
                  ...(n.atqDate ? [`ATQ: ${formatDate(n.atqDate)}`] : []),
                  ...(n.atsDate ? [`ATS: ${formatDate(n.atsDate)}`] : []),
                  ...(n.mrdDate ? [`MRD: ${formatDate(n.mrdDate)}`] : []),
                ];
                const tipH = lines.length * 14 + 12;

                // Flip above row if tooltip would overflow SVG bottom
                const belowY = y + ROW_HEIGHT + 2;
                const aboveY = Math.max(0, y - tipH - 2);
                const ty = (belowY + tipH > svgHeight) ? aboveY : belowY;

                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <rect
                      x={tx} y={ty} width={220} height={tipH} rx={6}
                      fill="#0f172a"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth={1}
                    />
                    {lines.map((line, li) => (
                      <text
                        key={li}
                        x={tx + 8}
                        y={ty + 14 + li * 14}
                        fill={li === 0 ? '#f1f5f9' : '#94a3b8'}
                        fontSize={li === 0 ? 11 : 10}
                        fontWeight={li === 0 ? 600 : 400}
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                );
              })()}
            </svg>
            </div>
          )}
        </div>
      </div>

      {/* NodeModal — double-click to edit */}
      {detailNodeId && (() => {
        const detailNode = allNodes.find((n) => n.id === detailNodeId);
        if (!detailNode) return null;
        return (
          <NodeModal node={detailNode} allNodes={allNodes}
            onClose={() => setDetailNodeId(null)}
            onSave={(id, changes) => { void updateNode(id, changes); }}
            onDelete={(id) => { void deleteNode(id); setDetailNodeId(null); }}
            onDuplicate={(id) => { void duplicateNode(id); }}
            onAddParent={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
            onAddChild={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
            onAddRelated={(a, b) => { void createRelatedLink(a, b); }}
            onRemoveRelationship={(a, b) => { void removeRelationship(a, b); }}
            onConvertEdgeType={(a, b, toType, parentId) => { void convertEdgeType(a, b, toType, parentId); }}
          />
        );
      })()}
    </div>
  );
}
